import { useEffect, useId, useMemo, useRef, useState } from "react";

export interface CompactMultiSelectOption {
  id: string;
  label: string;
  description?: string;
}

export function CompactMultiSelect({
  label,
  value,
  options,
  onChange,
  max,
  searchable = false,
  placeholder = "请选择（可多选）"
}: {
  label: string;
  value: string[];
  options: CompactMultiSelectOption[];
  onChange: (value: string[]) => void;
  max?: number;
  searchable?: boolean;
  placeholder?: string;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLFieldSetElement>(null);
  const menuId = useId();
  const selectedOptions = options.filter((option) => value.includes(option.id));
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleOptions = useMemo(
    () => options.filter((option) =>
      !normalizedQuery || `${option.label}${option.description ?? ""}`.toLocaleLowerCase().includes(normalizedQuery)
    ),
    [normalizedQuery, options]
  );
  const atLimit = max !== undefined && value.length >= max;

  useEffect(() => {
    if (!open) return;
    function closeOnOutsideClick(event: MouseEvent): void {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    function closeOnEscape(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  function toggle(id: string, checked: boolean): void {
    if (!checked) {
      onChange(value.filter((item) => item !== id));
      return;
    }
    if (max === 1) {
      onChange([id]);
      return;
    }
    if (!value.includes(id) && !atLimit) onChange([...value, id]);
  }

  return (
    <fieldset ref={rootRef} className="resource-multi-select">
      <legend>{label}</legend>
      <button
        className="resource-multi-select-control"
        type="button"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={`${label}，已选 ${value.length} 项`}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="resource-multi-select-values">
          {selectedOptions.length
            ? selectedOptions.map((option) => <span className="resource-selected-chip" key={option.id}>{option.label}</span>)
            : <span className="resource-multi-select-placeholder">{placeholder}</span>}
        </span>
        <span className="resource-multi-select-meta">
          {max ? `${value.length}/${max}` : `已选 ${value.length}`}
          <span aria-hidden="true">⌄</span>
        </span>
      </button>
      {open ? (
        <div id={menuId} className="resource-multi-select-menu" role="group" aria-label={`${label}选项`}>
          {searchable ? (
            <label className="resource-multi-select-search">
              <span className="visually-hidden">搜索{label}</span>
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={`搜索${label}`}
              />
            </label>
          ) : null}
          <div className="resource-multi-select-options">
            {visibleOptions.map((option) => {
              const checked = value.includes(option.id);
              return (
                <label key={option.id}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!checked && max !== 1 && atLimit}
                    onChange={(event) => toggle(option.id, event.target.checked)}
                  />
                  <span>
                    <strong>{option.label}</strong>
                    {option.description ? <small>{option.description}</small> : null}
                  </span>
                </label>
              );
            })}
            {!visibleOptions.length ? <p>没有匹配选项</p> : null}
          </div>
          {atLimit && max !== 1 ? <small className="field-hint">最多选择 {max} 项</small> : null}
        </div>
      ) : null}
    </fieldset>
  );
}
